import React, { useMemo, useState } from 'react'
import { v4 as uuidv4 } from 'uuid'
import { documents } from '@homevest/utils'
import {
  Alert,
  AlertIcon,
  Box,
  Button,
  FormControl,
  FormLabel,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Stack,
  Text,
  useToast
} from '@chakra-ui/react'

import axios from 'lib/axios'
import { UploadContainer } from 'components/Toolkit'

const { DOCUMENT_TYPES, DOCUMENT_EXTERNAL_SOURCES } = documents

const USER_DOCUMENT_TYPES = [
  {
    label: 'Identity Document',
    value: DOCUMENT_TYPES.IDENTITY_DOCUMENT
  },
  {
    label: 'Bank Statement',
    value: DOCUMENT_TYPES.BANK_STATEMENT
  },
  {
    label: 'Supplemental Document',
    value: DOCUMENT_TYPES.SUPPLEMENTAL_DOCUMENT
  },
  {
    label: 'Credit Report',
    value: DOCUMENT_TYPES.CREDIT_REPORT
  },
  {
    label: 'Criminal Report',
    value: DOCUMENT_TYPES.CRIMINAL_REPORT
  },
  {
    label: 'Eviction Report',
    value: DOCUMENT_TYPES.EVICTION_REPORT
  },
  {
    label: 'Other Financial Document',
    value: DOCUMENT_TYPES.OTHER_FINANCIAL_DOCUMENT
  }
]

type UploadState = {
  isLoading?: boolean
  file?: File
  error?: any
  downloadUrl?: string
  uploadPath?: string
}

type Props = {
  isOpen: boolean
  onClose: () => void
  onUploaded: () => void
  userId: string
}

const UploadDropzone: React.FC<
  React.PropsWithChildren<{
    isDisabled: boolean
    isLoading?: boolean
  }>
> = ({ children, isDisabled, isLoading }) => (
  <Box
    borderWidth='1px'
    borderColor={isDisabled ? 'gray.200' : 'gray.300'}
    borderRadius='md'
    bg={isDisabled ? 'gray.50' : 'white'}
    color={isDisabled ? 'gray.400' : 'gray.700'}
    cursor={isDisabled || isLoading ? 'not-allowed' : 'pointer'}
    px={4}
    py={8}
    textAlign='center'
    transition='background-color 0.2s ease'
    _hover={isDisabled || isLoading ? undefined : { bg: 'gray.50' }}
  >
    {children}
  </Box>
)

const normalizeUploadError = (err: any): string =>
  err?.response?.data?.message ?? err?.response?.data?.error ?? err?.message ?? 'Upload failed'

const UploadDocumentModal: React.FC<React.PropsWithChildren<Props>> = ({
  isOpen,
  onClose,
  onUploaded,
  userId
}) => {
  const toast = useToast()
  const [documentType, setDocumentType] = useState('')
  const [uploadId, setUploadId] = useState(uuidv4())
  const [uploadState, setUploadState] = useState<UploadState>()
  const [isCreatingDocument, setIsCreatingDocument] = useState(false)
  const [error, setError] = useState('')

  const uploadPath = useMemo(
    () => (documentType ? `/users/${userId}/documents/${documentType}/${uploadId}` : ''),
    [documentType, uploadId, userId]
  )

  const resetState = () => {
    setDocumentType('')
    setUploadId(uuidv4())
    setUploadState(undefined)
    setIsCreatingDocument(false)
    setError('')
  }

  const isUploading = Boolean(uploadState?.isLoading || isCreatingDocument)
  const isUploadDisabled = !documentType || isUploading

  const handleClose = ({ force = false }: { force?: boolean } = {}) => {
    if (isUploading && !force) {
      return
    }

    resetState()
    onClose()
  }

  const handleUploadStateChange = async (state: UploadState) => {
    setUploadState(state)

    if (state.isLoading) {
      setError('')
      return
    }

    if (state.error) {
      setError(normalizeUploadError(state.error))
      setIsCreatingDocument(false)
      return
    }

    if (
      !state.downloadUrl ||
      !documentType ||
      state.uploadPath !== uploadPath ||
      isCreatingDocument
    ) {
      return
    }

    try {
      setIsCreatingDocument(true)
      setError('')
      await axios.post('/admin/documents', {
        user_id: userId,
        type: documentType,
        external_id: uploadPath.substring(1),
        external_source: DOCUMENT_EXTERNAL_SOURCES.GOOGLE_CLOUD
      })
      toast({
        title: 'Document uploaded',
        status: 'success',
        duration: 5000,
        isClosable: true,
        position: 'bottom-right'
      })
      onUploaded()
      handleClose({ force: true })
    } catch (err: any) {
      const message = normalizeUploadError(err)
      setError(message)
      toast({
        title: 'Error uploading document',
        description: message,
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'bottom-right'
      })
      setIsCreatingDocument(false)
    }
  }

  return (
    <Modal
      closeOnEsc={!isUploading}
      closeOnOverlayClick={!isUploading}
      isCentered
      isOpen={isOpen}
      onClose={() => handleClose()}
      size='lg'
    >
      <ModalOverlay bg='blackAlpha.500' />
      <ModalContent>
        <ModalHeader>Upload Document</ModalHeader>
        <ModalCloseButton isDisabled={isUploading} />
        <ModalBody>
          <Stack spacing={4}>
            {error ? (
              <Alert status='error' borderRadius='md'>
                <AlertIcon />
                {error}
              </Alert>
            ) : null}
            <FormControl isRequired>
              <FormLabel>Document type</FormLabel>
              <Select
                isDisabled={isUploading}
                placeholder='Choose a document type'
                value={documentType}
                onChange={(event) => {
                  if (isUploading) {
                    return
                  }

                  setDocumentType(event.target.value)
                  setUploadId(uuidv4())
                  setUploadState(undefined)
                  setError('')
                }}
              >
                {USER_DOCUMENT_TYPES.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Select>
            </FormControl>
            {documentType ? (
              <UploadContainer
                isDisabled={isUploadDisabled}
                onChange={handleUploadStateChange as any}
                uploadPath={uploadPath}
              >
                <UploadDropzone isDisabled={isUploadDisabled} isLoading={isUploading}>
                  <Stack spacing={1}>
                    <Text fontWeight='medium'>
                      {isUploading
                        ? `Uploading ${uploadState?.file?.name ?? 'document'}...`
                        : 'Choose a file or drag it here'}
                    </Text>
                    <Text color='gray.500' fontSize='sm'>
                      The document will upload as soon as a file is selected.
                    </Text>
                  </Stack>
                </UploadDropzone>
              </UploadContainer>
            ) : (
              <UploadDropzone isDisabled isLoading={false}>
                <Stack spacing={1}>
                  <Text fontWeight='medium'>Choose a document type first</Text>
                  <Text color='gray.500' fontSize='sm'>
                    The file picker unlocks after the document type is selected.
                  </Text>
                </Stack>
              </UploadDropzone>
            )}
          </Stack>
        </ModalBody>
        <ModalFooter>
          <Button variant='outline' onClick={() => handleClose()} isDisabled={isUploading}>
            Close
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}

export default UploadDocumentModal
